/**
 * @module  public/js/services/twilioEdgeConfiguration.js
 * @summary Resolve the default Twilio Voice edge(s) for browser clients.
 *
 * @description
 *   Reads configuration exposed on `globalThis` (for example via `window.env`
 *   or custom globals) and normalises the value into a format accepted by the
 *   Twilio Voice `Device` constructor. If no explicit configuration is found,
 *   the module falls back to the Ashburn edge, which offers the lowest latency
 *   for the majority of North American users.
 */

const g = typeof globalThis !== 'undefined' ? globalThis : {};

/** @type {unknown} */
const rawEdge =
  g.twilioVoiceEdge ??
  g.TWILIO_VOICE_EDGE ??
  g.env?.TWILIO_VOICE_EDGE ??
  g.topsail?.twilioVoiceEdge ??
  g.config?.twilioVoiceEdge ??
  null;

/** @type {string | string[] | null} */
let resolvedEdge = null;

if (Array.isArray(rawEdge)) {
  const cleaned = rawEdge
    .map((value) =>
      typeof value === 'string' ? value.trim() : String(value ?? '').trim()
    )
    .filter(Boolean);
  if (cleaned.length === 1) {
    resolvedEdge = cleaned[0];
  } else if (cleaned.length > 1) {
    resolvedEdge = cleaned;
  }
} else if (typeof rawEdge === 'string') {
  const trimmed = rawEdge.trim();
  if (trimmed.includes(',')) {
    const entries = trimmed
      .split(',')
      .map((entry) => entry.trim())
      .filter(Boolean);
    if (entries.length === 1) {
      resolvedEdge = entries[0];
    } else if (entries.length > 1) {
      resolvedEdge = entries;
    }
  } else if (trimmed) {
    resolvedEdge = trimmed;
  }
} else if (rawEdge != null) {
  const asString = String(rawEdge).trim();
  if (asString) {
    resolvedEdge = asString;
  }
}

if (Array.isArray(resolvedEdge) && resolvedEdge.length === 0) {
  resolvedEdge = null;
}

export const DEFAULT_TWILIO_EDGE = resolvedEdge ?? 'ashburn';

export default DEFAULT_TWILIO_EDGE;
